import {
  Button,
  Link as MuiLink,
  styled,
  TextField,
  Typography,
} from "@mui/material"
import { useSnackbar } from "notistack"
import type React from "react"
import { useCallback, useState } from "react"
import { Link, Navigate } from "react-router"

import PhcAshcaLarge from "../assets/svg-components/PhcAshcaLarge"
import useAuth from "../utils/auth"
import { ROUTES } from "../utils/constants"
import { extraColors } from "../utils/theme"

const Content = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100vh",
  backgroundColor: `linear-gradient(180deg, ${extraColors.dark} 66%, black 100%)`,
}))

const Card = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  width: 360,
  borderRadius: 6,
  padding: "18px 24px 24px",
  textAlign: "center",
  marginTop: 60,
  display: "grid",
  gap: theme.spacing(3),
}))

const ButtonWrapper = styled("div")({
  marginTop: 24,
  display: "flex",
  justifyContent: "space-between",
})

const SignIn: React.FC = () => {
  const { loginWithRedirect, isAuthenticated } = useAuth()
  const { enqueueSnackbar } = useSnackbar()

  const [loginEmail, setLoginEmail] = useState<string>("")
  const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false)

  const handleChange = (val: string) => {
    setLoginEmail(val)
  }

  const handleSubmit = useCallback(async () => {
    const initialUrl = localStorage.getItem("initialUrl") ?? "/"
    localStorage.removeItem("initialUrl")
    setIsLoggingIn(true)

    try {
      await loginWithRedirect({
        login_hint: loginEmail,
        returnTo: initialUrl,
      })
    } catch {
      enqueueSnackbar("Failed to initiate login. Please try again.", {
        variant: "error",
      })
      setIsLoggingIn(false)
    }
  }, [enqueueSnackbar, loginWithRedirect, loginEmail])

  if (isAuthenticated) {
    return <Navigate replace to={ROUTES.BASE} />
  }

  return (
    <Content>
      <Card data-testid="card">
        <PhcAshcaLarge
          style={{ height: 60, width: "auto", margin: "0 auto" }}
        />
        <Typography component="h1" variant="body1" data-testid="title">
          Enter your email to Sign In
        </Typography>
        <form
          onSubmit={e => {
            e.preventDefault()
            handleSubmit().catch(console.error)
          }}
        >
          <TextField
            type="email"
            name="email"
            label="Email"
            id="username-input"
            onChange={e => handleChange(e.target.value)}
            disabled={isLoggingIn}
            value={loginEmail}
            fullWidth
          />
          <Button
            data-testid="sign-in-button"
            type="submit"
            variant="contained"
            color="primary"
            disabled={isLoggingIn}
            fullWidth
            sx={{
              marginTop: 2,
            }}
          >
            {isLoggingIn ? "Loading..." : "Sign In"}
          </Button>
          <ButtonWrapper>
            <MuiLink
              component={Link}
              to={ROUTES.PRIVACY_POLICY}
              data-testid="privacy-button"
              sx={{
                textTransform: "uppercase",
                fontSize: 8,
              }}
            >
              Privacy and Accessibility Policy
            </MuiLink>
            <MuiLink
              component={Link}
              to={ROUTES.TERMS_AND_CONDITIONS}
              data-testid="terms-button"
              sx={{
                textTransform: "uppercase",
                fontSize: 8,
              }}
            >
              Terms & Conditions
            </MuiLink>
          </ButtonWrapper>
        </form>
      </Card>
    </Content>
  )
}

export default SignIn
